import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { EventType } from '@/utils/eventUtils';

export type SharePlatform = 
  | 'whatsapp' 
  | 'facebook' 
  | 'linkedin' 
  | 'twitter' 
  | 'instagram'
  | 'telegram' 
  | 'email' 
  | 'copy_link' 
  | 'copy_text';

interface ShareEventParams {
  eventId: string;
  eventType: EventType;
  platform: SharePlatform;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export function useEventSharing() {
  const trackShare = async (params: ShareEventParams) => {
    try {
      await supabase.from('event_shares').insert({
        event_id: params.eventId,
        event_type: params.eventType,
        platform: params.platform,
        utm_source: params.utmSource || params.platform,
        utm_medium: params.utmMedium || 'social',
        utm_campaign: params.utmCampaign,
        referrer: typeof window !== 'undefined' ? window.location.href : null,
      });
    } catch (error) {
      console.error('Failed to track share:', error);
    }
  };

  const generateEventUrl = (eventId: string, eventType: string, platform: SharePlatform) => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    // Map event type to category segment used by the route /events/:category/:id
    const categoryMap: Record<string, string> = {
      festival: 'festivals',
      academy: 'academies',
      retreat: 'retreats',
      workation: 'workations',
      venue: 'venues',
      coliving: 'colivings',
    };
    const category = categoryMap[eventType] || `${eventType}s`;
    const eventPath = `/events/${category}/${eventId}`;
    return `${baseUrl}${eventPath}?utm_source=${platform}&utm_medium=social&utm_campaign=event_share`;
  };

  const generateShareText = (eventName: string, location: string, startDate: string, endDate: string, description: string, url: string) => {
    const shortDesc = description?.substring(0, 150) || '';
    return `🎉 ${eventName}\n📍 ${location}\n📅 ${startDate} - ${endDate}\n\n${shortDesc}...\n\n👉 Learn more: ${url}`;
  };

  const shareToWhatsApp = (text: string) => {
    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
  };

  const shareToFacebook = (url: string, quote: string) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedQuote = encodeURIComponent(quote);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedQuote}`, '_blank');
  };

  const shareToLinkedIn = (url: string) => {
    const encodedUrl = encodeURIComponent(url);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank');
  };

  const shareToTwitter = (url: string, text: string) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);
    window.open(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`, '_blank');
  };

  const shareToTelegram = (url: string, text: string) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);
    window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, '_blank');
  };

  const shareViaEmail = (subject: string, body: string) => {
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    window.location.href = `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
  };

  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  return {
    trackShare,
    generateEventUrl,
    generateShareText,
    shareToWhatsApp,
    shareToFacebook,
    shareToLinkedIn,
    shareToTwitter,
    shareToTelegram,
    shareViaEmail,
    copyToClipboard,
  };
}
